import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, CaretRight, Heart } from '@phosphor-icons/react';
import { plural, PLACES } from '../utils/plural';
import telegramService from '../services/telegram';

/**
 * Плашка подборки — строка списка, один каркас с плашкой маршрута.
 *
 * Отличается от маршрута тем, чем отличается сама сущность: у подборки
 * нет ни времени, ни расстояния — только число мест. Поэтому метрик
 * в строку не набирается, и вместо них идёт описание одной строкой:
 * подборки называют коротко («С детьми»), и без пояснения название
 * мало что говорит.
 *
 * Описание было в две строки при фото 160px во всю ширину — плашка
 * весила как половина экрана. Обрезано до одной строки.
 *
 * Кнопки внутри нет: у подборки нет состояний, клик по площади.
 *
 * `onUnsave` передают там, где плашка стоит в списке сохранённого —
 * убрать из него хотят прямо из списка, а не заходя в подборку.
 */
const CollectionCard = ({ collection, onUnsave }) => {
  const navigate = useNavigate();
  if (!collection) return null;

  const imageUrl = collection.image?.url || null;
  const placesCount = collection.places?.length || 0;

  return (
    <div
      onClick={() => {
        telegramService.hapticFeedback('light');
        navigate(`/collection/${collection.slug}`);
      }}
      className="mb-2 flex cursor-pointer items-center gap-3 overflow-hidden rounded-lg border border-theme-border bg-theme-card p-3 transition-transform duration-fast ease-out active:scale-98"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <div className="h-[72px] w-[72px] flex-shrink-0 overflow-hidden rounded-md">
        {imageUrl ? (
          <img src={imageUrl} alt={collection.title} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-theme-card-secondary">
            <BookOpen size={24} weight="light" className="text-theme-text-hint" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <h3 className="truncate text-body font-semibold text-theme-text-primary">
          {collection.title}
        </h3>

        {collection.description && (
          <p className="mt-0.5 truncate text-caption text-theme-text-secondary">
            {collection.description}
          </p>
        )}

        {placesCount > 0 && (
          <p className="tabular mt-1 text-caption text-theme-text-hint">
            {placesCount} {plural(placesCount, PLACES)}
          </p>
        )}
      </div>

      {/* Сердце, как у карточки места: пользователь видит один список
          сохранённого, и разные глифы для «убрать» в нём читались бы
          разными действиями. Пара тон-в-тон `favorite-bg` / `favorite-text` —
          та же, что у кнопки избранного вне фотографии */}
      {onUnsave && (
        <button
          onClick={e => { e.stopPropagation(); telegramService.hapticFeedback('light'); onUnsave(); }}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center border-0 bg-transparent transition-transform duration-fast ease-out active:scale-90"
          style={{ WebkitTapHighlightColor: 'transparent' }}
          aria-label={`Убрать «${collection.title}» из сохранённых`}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-theme-favorite-bg text-theme-favorite-text">
            <Heart size={16} weight="fill" />
          </span>
        </button>
      )}

      <CaretRight size={18} className={`flex-shrink-0 text-theme-text-hint ${onUnsave ? '-ml-1' : ''}`} />
    </div>
  );
};

export default CollectionCard;
